import { RewardsPage } from "./RewardsPage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { api, clearCsrfToken } from "../../shared/api/client";
import type { ClassState } from "../../shared/api/types";
import { BRAND_NAME, BRAND_TAGLINE } from "../../shared/brand";
import { image } from "../../shared/assets/manifest";
import { awardIconSrc, groupBadgeIconSrc } from "../../shared/assets/media";
import { growthInfo, studentFaceSrc } from "../../shared/growth";
import { classroomProjectionUrl, drawQrToCanvas } from "../../shared/qr";
import { Button } from "../../shared/ui/Button";
import { EmptyState } from "../../shared/ui/EmptyState";
import { LoadingBlock } from "../../shared/ui/LoadingBlock";
import { Modal } from "../../shared/ui/Modal";
import { useCanLeavePage } from "../../shared/ui/PageExitGuard";
import { Podium, RaceTrackList } from "../../shared/ui/CuteDecor";
import { useToast } from "../../shared/ui/ToastProvider";
import {
  AWARD_ORDER,
  awardLabel,
  canManageOrg,
  canWrite,
  categoryIconSrc,
  formatTime,
  groupScores,
  invalidateState,
  useApplyStudentPoints,
  watchStudents
} from "./actions";
import { ClassroomToolsPage } from "./ClassroomToolsPage";
import {
  OVERVIEW_DOCK_REWARDS,
  OVERVIEW_QUICK_ACTIONS,
  useClassroomBootstrap,
  useClassroomMode,
  useClassroomQuickSilent
} from "./classroomMode";
import { FollowUpPanel, GoalsPage } from "./GoalsPage";

const AttendancePage = lazy(() => import("./AttendancePage").then((module) => ({ default: module.AttendancePage })));
const CounselingPage = lazy(() => import("./CounselingPage").then((module) => ({ default: module.CounselingPage })));
const GradesPage = lazy(() => import("./GradesPage").then((module) => ({ default: module.GradesPage })));
const GrowthPage = lazy(() => import("./GrowthPage").then((module) => ({ default: module.GrowthPage })));
const HistoryPage = lazy(() => import("./HistoryPage").then((module) => ({ default: module.HistoryPage })));
const InsightsPage = lazy(() => import("./InsightsPage").then((module) => ({ default: module.InsightsPage })));
const MonthlyPage = lazy(() => import("./MonthlyPage").then((module) => ({ default: module.MonthlyPage })));
const PointsPage = lazy(() => import("./PointsPage").then((module) => ({ default: module.PointsPage })));
const ReportsPage = lazy(() => import("./ReportsPage").then((module) => ({ default: module.ReportsPage })));
const SettingsPage = lazy(() => import("./SettingsPage").then((module) => ({ default: module.SettingsPage })));
const StudentsPage = lazy(() => import("./StudentsPage").then((module) => ({ default: module.StudentsPage })));

const navItems = [
  ["overview", "总览", image.home],
  ["students", "学生", image.students],
  ["points", "积分", image.points],
  ["rewards", "奖品兑换", image.shortcutReward],
  ["leaderboard", "排行", image.leaderboard],
  ["history", "记录", image.history],
  ["counseling", "留痕", image.counseling],
  ["growth", "成长", image.growth],
  ["goals", "目标", image.growthFlower],
  ["attendance", "考勤", image.students],
  ["reports", "报告", image.monthly],
  ["grades", "成绩", image.grades],
  ["monthly", "月度", image.monthly],
  ["insights", "洞察", image.insights],
  ["classroom-tools", "课堂工具", image.classroom],
  ["settings", "设置", image.settings]
] as const;

const mobileNavItems = [
  ["/", "总览", image.mobileHome],
  ["/students", "学生", image.mobileStudents],
  ["/points", "积分", image.points],
  ["/grades", "成绩", image.monthly],
  ["/settings", "设置", image.mobileProfile]
] as const;

const mobileMoreItems = [
  ["/rewards", "奖品兑换", image.shortcutReward],
  ["/leaderboard", "排行", image.leaderboard],
  ["/history", "记录", image.history],
  ["/counseling", "留痕", image.counseling],
  ["/growth", "成长", image.growth],
  ["/goals", "目标", image.growthFlower],
  ["/attendance", "考勤", image.students],
  ["/reports", "报告", image.monthly],
  ["/monthly", "月度", image.monthly],
  ["/insights", "洞察", image.insights],
  ["/classroom-tools", "课堂工具", image.classroom]
] as const;

type QuickAction = {
  label: string;
  points: number;
  reason: string;
  icon?: string;
  tone?: string;
};

function Overview({ state }: { state: ClassState }) {
  const navigate = useNavigate();
  const toast = useToast();
  const writable = canWrite(state);
  const applyPoints = useApplyStudentPoints();
  const { enabled: classroomMode } = useClassroomMode();
  const { enabled: quickSilent } = useClassroomQuickSilent();
  const [picker, setPicker] = useState<QuickAction | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const qrRef = useRef<HTMLCanvasElement | null>(null);
  const overviewStats = useQuery({
    queryKey: ["overview-stats", state.classId],
    queryFn: async () => {
      const payload = await api<{
        todayStars: number;
        watchCount: number;
        studentCount: number;
        activeCount: number;
        totalPoints: number;
      }>("/api/overview-stats");
      return payload.data!;
    }
  });
  const today = new Date().toISOString().slice(0, 10);
  const todayAttendance = useQuery({
    queryKey: ["today-workbench-attendance", state.classId, today],
    enabled: state.features?.attendance === true,
    queryFn: async () => (await api<{ exists: boolean; markedCount: number; activeStudentCount: number; day?: { status: string } }>(`/api/attendance/days/${today}`)).data
  });
  const recentGrowth = useQuery({
    queryKey: ["today-workbench-growth", state.classId],
    queryFn: async () => (await api<{ items: { id: number; title: string; studentName?: string | null; happenedAt: string }[] }>("/api/growth-evidence?page=1&pageSize=3")).data?.items || []
  });

  const total = overviewStats.data?.totalPoints ?? state.students.reduce((sum, student) => sum + Number(student.points || 0), 0);
  const active = overviewStats.data?.activeCount ?? state.students.filter((student) => Number(student.points || 0) !== 0).length;
  const todayStars = overviewStats.data?.todayStars ?? 0;
  const watchCount = overviewStats.data?.watchCount ?? watchStudents(state.students, state.history).length;
  const topStudents = [...state.students].sort((a, b) => Number(b.points || 0) - Number(a.points || 0)).slice(0, 5);
  const groups = groupScores(state).slice(0, 5);
  const recentHistory = state.history.filter((record) => !record.voided).slice(0, 6);

  useEffect(() => {
    if (!qrRef.current) return;
    try {
      drawQrToCanvas(qrRef.current, classroomProjectionUrl(state), 72);
    } catch {
      // QR vendor may fail in rare environments; dock still usable.
    }
  }, [state.classId, state.className]);

  const applyToAll = async (action: QuickAction) => {
    const ids = state.students.map((student) => student.id);
    if (!ids.length) {
      toast("请先添加学生", "error");
      return;
    }
    await applyPoints.mutateAsync({
      studentIds: ids,
      amount: action.points,
      reason: action.reason,
      category: action.points > 0 ? "study" : "discipline"
    });
  };

  const runQuick = async (action: QuickAction) => {
    if (!writable) {
      toast("当前账号为只读权限", "error");
      return;
    }
    if (classroomMode && quickSilent) {
      await applyToAll(action);
      return;
    }
    setChecked(new Set());
    setPicker(action);
  };

  return (
    <div className="dashboard-grid overview-page">
      <section className="metric-grid">
        <article className="metric-card">
          <span>今日星星</span>
          <img className="metric-icon" src={image.statStar} alt="" />
          <strong>+{todayStars}</strong>
        </article>
        <article className="metric-card green">
          <span>班级总分</span>
          <img className="metric-icon" src={image.statFlower} alt="" />
          <strong>{total}</strong>
        </article>
        <article className="metric-card blue">
          <span>活跃同学</span>
          <img className="metric-icon" src={image.statPeople} alt="" />
          <strong>
            {active}/{state.students.length}
          </strong>
        </article>
        <article className="metric-card pink">
          <span>待关注</span>
          <img className="metric-icon" src={image.statHeart} alt="" />
          <strong>{watchCount}</strong>
        </article>
      </section>

      <FollowUpPanel state={state} compact />

      <section className="surface-panel today-workbench">
        <div className="panel-heading">
          <div><h2>今日工作台</h2><span>先完成必须处理的事项，再回顾近期记录</span></div>
        </div>
        <div className="today-workbench-grid">
          <button type="button" onClick={() => navigate("/attendance")} disabled={!state.features?.attendance}>
            <strong>{todayAttendance.data?.day?.status === "finalized" ? "考勤已完成" : "待完成考勤"}</strong>
            <span>{todayAttendance.data?.markedCount || 0}/{todayAttendance.data?.activeStudentCount || state.students.length} 人已标记</span>
          </button>
          <button type="button" onClick={() => navigate("/goals")} disabled={!state.features?.goals}>
            <strong>目标跟进</strong><span>查看逾期目标、风险检查和到期干预</span>
          </button>
          <div className="today-recent-growth">
            <strong>近期成长记录</strong>
            {(recentGrowth.data || []).length ? recentGrowth.data!.map((item) => <span key={item.id}>{item.studentName || "班级"} · {item.title}</span>) : <span>暂无记录</span>}
          </div>
        </div>
      </section>

      <div className="overview-main-col">
        <section className="surface-panel overview-quick-panel">
          <div className="panel-heading">
            <h2>
              <img className="panel-title-icon" src={image.statStar} alt="" />
              快捷记分
            </h2>
            <span>{classroomMode && quickSilent ? "课堂静默：点一下给全班" : "选择学生后记分"}</span>
          </div>
          <div className="quick-score-grid">
            {OVERVIEW_QUICK_ACTIONS.map((item) => (
              <button
                key={item.label}
                type="button"
                className={`quick-score-card ${item.tone || ""}`}
                disabled={!writable || applyPoints.isPending}
                onClick={() => runQuick(item).catch(() => {})}
              >
                <img src={item.icon} alt="" />
                <span className="quick-score-value">{item.points > 0 ? `+${item.points}` : item.points}</span>
                <span className="quick-score-label">{item.label}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="surface-panel overview-garden-panel">
          <div className="panel-heading">
            <h2>
              <img className="panel-title-icon" src={image.growthFlower} alt="" />
              成长花园
            </h2>
            <Button variant="secondary" size="small" type="button" onClick={() => navigate("/leaderboard")}>
              完整排行
            </Button>
          </div>
          {topStudents.length ? (
            <div className="growth-card-grid">
              {topStudents.map((student) => {
                const growth = growthInfo(student, state.growthLimits);
                return (
                  <article className="growth-card" key={student.id}>
                    <div className="growth-card-plant">
                      <img className="growth-plant-img" src={growth.imageSrc} alt="" title={growth.label} />
                    </div>
                    <div className="growth-card-main">
                      <strong className="student-name">{student.name}</strong>
                      <span className="student-meta">{growth.label}</span>
                      <b className="growth-value">
                        <img className="growth-score-star" src={image.statStar} alt="" />
                        {student.points}
                      </b>
                      <div className="cute-progress" aria-hidden="true">
                        <span className="cute-progress-fill" style={{ width: `${growth.progress}%` }} />
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <EmptyState title="暂无学生" detail="添加学生后会显示成长状态。" />
          )}
        </section>

        <section className="surface-panel overview-rank-panel">
          <div className="panel-heading">
            <h2>
              <img className="panel-title-icon" src={image.groupBadge} alt="" />
              小组赛跑
            </h2>
            <span>实时排名</span>
          </div>
          {groups.length ? (
            <RaceTrackList
              groups={groups.map((group) => ({
                id: group.id,
                name: group.name,
                total: group.total,
                color: group.color,
                count: group.count,
                avg: group.avg
              }))}
            />
          ) : (
            <EmptyState title="暂无小组" detail="创建小组后会显示排名。" imageSrc={image.groupBadge} />
          )}
        </section>
      </div>

      <aside className="overview-side-col">
        <section className="surface-panel overview-history-panel notebook-panel">
          <div className="panel-heading">
            <h2>
              <img className="panel-title-icon" src={image.historyNotebook} alt="" />
              最近记录
            </h2>
            <span>最新 {recentHistory.length} 条</span>
          </div>
          <span className="washi-tape" aria-hidden="true" />
          {recentHistory.length ? (
            <div className="notebook-list">
              {recentHistory.map((record) => (
                <article className="notebook-row" key={record.id}>
                  <img
                    className="mini-face"
                    src={
                      record.studentId
                        ? studentFaceSrc(
                            state.students.find((item) => item.id === record.studentId) || { id: record.studentId },
                            state.growthLimits
                          )
                        : image.navGroups
                    }
                    alt=""
                  />
                  <div className="notebook-main">
                    <strong>{record.studentName || record.groupName || "班级"}</strong>
                    <span className="history-reason">
                      <img className="preset-inline-icon" src={categoryIconSrc(record.category || "manual")} alt="" />
                      {record.reason || "记分"}
                    </span>
                  </div>
                  <div className="notebook-meta">
                    <b className={Number(record.amount) < 0 ? "neg" : "pos"}>
                      {Number(record.amount) > 0 ? `+${record.amount}` : record.amount}
                    </b>
                    <small>{formatTime(record.time)}</small>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState title="暂无记录" detail="记分后会出现在这里。" imageSrc={image.historyNotebook} />
          )}
          <div className="panel-actions wrap" style={{ marginTop: 10 }}>
            <Button variant="secondary" size="small" type="button" onClick={() => navigate("/history")}>
              查看全部记录
            </Button>
          </div>
        </section>

        <section className="surface-panel overview-awards-panel">
          <div className="panel-heading">
            <h2>
              <img className="panel-title-icon" src={image.trophy} alt="" />
              月度荣誉
            </h2>
            <Button variant="secondary" size="small" type="button" onClick={() => navigate("/monthly")}>
              查看月度
            </Button>
          </div>
          <div className="overview-award-grid">
            {AWARD_ORDER.map((type) => (
              <article className="overview-award-card" key={type}>
                <img className="overview-award-img" src={awardIconSrc(type)} alt="" />
                <strong>{awardLabel({ awardType: type })}</strong>
              </article>
            ))}
          </div>
        </section>
      </aside>

      <section className="surface-panel wide overview-dock">
        <div className="panel-heading">
          <h2>
            <img className="panel-title-icon" src={image.dockSign} alt="" />
            课堂奖励坞
          </h2>
          <span>投屏扫码进入课堂模式</span>
        </div>
        <img
          className="overview-dock-mascot overview-dock-mascot-left"
          src={image.dockBunny}
          alt=""
        />
        <img
          className="overview-dock-mascot overview-dock-mascot-right"
          src={image.dockBear}
          alt=""
        />
        <div className="overview-dock-body">
          {OVERVIEW_DOCK_REWARDS.map((item) => (
            <button
              key={item.label}
              type="button"
              className="overview-dock-card"
              disabled={!writable || applyPoints.isPending}
              onClick={() => runQuick(item).catch(() => {})}
            >
              <img src={item.icon} alt="" />
              <strong>{item.label}</strong>
              <span>+{item.points}</span>
            </button>
          ))}
          <button
            type="button"
            className="overview-dock-card reminder"
            disabled={!writable || applyPoints.isPending}
            onClick={() =>
              runQuick({ label: "提醒", points: -1, reason: "课堂提醒", icon: image.bell }).catch(() => {})
            }
          >
            <img src={image.bell} alt="" />
            <strong>提醒一下</strong>
            <span>-1</span>
          </button>
          <div className="overview-dock-qr" aria-label="课堂投屏码">
            <span>扫码进课堂</span>
            <div className="overview-qr-frame">
              <canvas ref={qrRef} className="overview-qr-canvas" width={72} height={72} aria-label="扫码进入课堂模式" />
              <img className="overview-qr-mascot" src={image.mascot} alt="" />
            </div>
          </div>
        </div>
      </section>

      {picker ? (
        <Modal
          title={`${picker.label} · 选择学生`}
          onClose={() => setPicker(null)}
          variant="picker"
          footer={
            <>
              <Button variant="secondary" type="button" onClick={() => setChecked(new Set(state.students.map((s) => s.id)))}>
                全选
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={applyPoints.isPending}
                onClick={() => {
                  if (!checked.size) {
                    toast("请选择学生", "error");
                    return;
                  }
                  applyPoints
                    .mutateAsync({
                      studentIds: Array.from(checked),
                      amount: picker.points,
                      reason: picker.reason,
                      category: "manual"
                    })
                    .then(() => setPicker(null))
                    .catch(() => {});
                }}
              >
                {applyPoints.isPending ? "提交中" : `确认（${checked.size}）`}
              </Button>
            </>
          }
        >
          <div className="picker-list">
            {state.students.map((student) => (
              <label className="checkbox-row" key={student.id}>
                <input
                  type="checkbox"
                  checked={checked.has(student.id)}
                  onChange={() => {
                    setChecked((current) => {
                      const next = new Set(current);
                      if (next.has(student.id)) next.delete(student.id);
                      else next.add(student.id);
                      return next;
                    });
                  }}
                />
                {student.name} · {student.points} 分
              </label>
            ))}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function Leaderboard({ state }: { state: ClassState }) {
  const students = [...state.students].sort((a, b) => Number(b.points || 0) - Number(a.points || 0));
  const groups = groupScores(state);
  return (
    <div className="dashboard-grid">
      <section className="surface-panel">
        <div className="panel-heading">
          <h2>
            <img className="panel-title-icon" src={image.leaderboard} alt="" />
            学生排行
          </h2>
          <span>{students.length} 人</span>
        </div>
        <Podium
          entries={students.map((student) => ({
            id: student.id,
            name: student.name,
            score: Number(student.points || 0),
            faceSrc: studentFaceSrc(student, state.growthLimits)
          }))}
          emptyTitle="暂无学生"
          emptyDetail="添加学生后会显示个人排行。"
        />
        <div className="rank-list">
          {students.slice(3).map((student, index) => (
            <article key={student.id}>
              <img className="mini-face" src={studentFaceSrc(student, state.growthLimits)} alt="" />
              <strong>
                <span className="rank-index">{index + 4}.</span> {student.name}
              </strong>
              <b>{student.points}</b>
            </article>
          ))}
        </div>
        {!students.length ? <EmptyState title="暂无学生" detail="添加学生后会显示个人排行。" /> : null}
      </section>
      <section className="surface-panel">
        <div className="panel-heading">
          <h2>
            <img className="panel-title-icon" src={image.groupBadge} alt="" />
            小组排行
          </h2>
          <span>{groups.length} 组</span>
        </div>
        <Podium
          entries={groups.map((group, index) => ({
            id: group.id,
            name: group.name,
            score: Number(group.total || 0),
            faceSrc: groupBadgeIconSrc(index)
          }))}
          emptyTitle="暂无小组"
          emptyDetail="创建小组后会显示小组排行。"
        />
        <div className="rank-list">
          {groups.slice(3).map((group, index) => (
            <article key={group.id}>
              <img className="group-badge-icon" src={groupBadgeIconSrc(index + 3)} alt="" />
              <strong>{group.name}</strong>
              <b>{group.total}</b>
            </article>
          ))}
        </div>
        {!groups.length ? <EmptyState title="暂无小组" detail="创建小组后会显示小组排行。" /> : null}
      </section>
    </div>
  );
}

export function DashboardPage() {
  const canLeavePage = useCanLeavePage();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { enabled: classroomMode, setEnabled: setClassroomMode, toggle: toggleClassroomMode } = useClassroomMode();
  const { enabled: quickSilent } = useClassroomQuickSilent();
  const applyPoints = useApplyStudentPoints();

  const stateQuery = useQuery({
    queryKey: ["state"],
    queryFn: async () => api<ClassState>("/api/state"),
    select: (payload) => payload.data as ClassState
  });

  const switchClass = useMutation({
    mutationFn: (classId: number) => api(`/api/classes/${classId}/switch`, { method: "POST" }),
    onSuccess: () => {
      invalidateState(queryClient);
      toast("已切换班级", "success");
    }
  });

  const onSwitchClass = useCallback(
    (classId: number) => {
      if (stateQuery.data?.classId !== classId && canLeavePage()) switchClass.mutate(classId);
    },
    [stateQuery.data?.classId, switchClass, canLeavePage]
  );

  useClassroomBootstrap(onSwitchClass);

  const logout = useMutation({
    mutationFn: () => api("/api/auth/logout", { method: "POST" }),
    onSuccess: async () => {
      clearCsrfToken();
      await queryClient.clear();
      window.location.href = "/";
    }
  });

  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    if (classroomMode) setMoreOpen(false);
  }, [classroomMode]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement | null)?.tagName || "";
      const typing = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (event.target as HTMLElement | null)?.isContentEditable;
      if (typing) return;
      if (event.key === "Escape" && moreOpen) {
        setMoreOpen(false);
        return;
      }
      if (!classroomMode) return;
      if (event.key === "Escape") {
        setClassroomMode(false);
        return;
      }
      const state = stateQuery.data;
      if (!state || !canWrite(state)) return;
      if (location.pathname !== "/" && location.pathname !== "") return;
      const index = Number(event.key) - 1;
      if (index < 0 || index >= OVERVIEW_QUICK_ACTIONS.length) return;
      event.preventDefault();
      const action = OVERVIEW_QUICK_ACTIONS[index];
      if (quickSilent) {
        const ids = state.students.map((student) => student.id);
        if (!ids.length) {
          toast("请先添加学生", "error");
          return;
        }
        applyPoints.mutate({
          studentIds: ids,
          amount: action.points,
          reason: action.reason,
          category: action.points > 0 ? "study" : "discipline"
        });
      } else {
        navigate("/");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [applyPoints, classroomMode, location.pathname, moreOpen, navigate, quickSilent, setClassroomMode, stateQuery.data, toast]);

  if (stateQuery.isLoading) return <LoadingBlock />;
  if (stateQuery.error || !stateQuery.data) {
    return <EmptyState title="无法加载班级数据" detail={stateQuery.error instanceof Error ? stateQuery.error.message : ""} />;
  }
  const state = stateQuery.data;
  const visibleNavItems = navItems.filter(([path]) =>
    (path !== "goals" || state.features?.goals) && (path !== "attendance" || state.features?.attendance)
  );
  const visibleMoreItems = mobileMoreItems.filter(([path]) =>
    (path !== "/goals" || state.features?.goals) && (path !== "/attendance" || state.features?.attendance)
  );
  const moreActive = visibleMoreItems.some(
    ([path]) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  return (
    <div className={`app-frame${classroomMode ? " classroom-mode" : ""}`}>
      <aside className="app-sidebar">
        <div className="brand-lockup">
          <img src={image.star} alt="" />
          <strong>
            {BRAND_NAME}
            <small>{BRAND_TAGLINE}</small>
          </strong>
          <img className="brand-rainbow" src={image.flagRainbow} alt="" />
        </div>
        <nav className="app-nav" aria-label="主导航">
          {visibleNavItems.map(([path, label, icon]) => (
            <NavLink key={path} to={path === "overview" ? "/" : `/${path}`} end={path === "overview"}>
              <img src={icon} alt="" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="app-main">
        <header className="app-topbar">
          <div>
            <span className="eyebrow">星星班级</span>
            <h1>{state.className || "一年级"}</h1>
            {(state.classes || []).length > 1 ? (
              <label className="topbar-class-switch">
                <span className="sr-only">切换班级</span>
                <select
                  value={state.classId || ""}
                  disabled={switchClass.isPending}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (next) onSwitchClass(next);
                  }}
                >
                  {(state.classes || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
          <div className="topbar-actions">
            <label className="classroom-mode-toggle">
              <input
                type="checkbox"
                checked={classroomMode}
                onChange={(event) => setClassroomMode(event.target.checked)}
              />
              <span className="classroom-mode-switch" aria-hidden="true" />
              <span>课堂模式</span>
            </label>
            {classroomMode ? (
              <Button variant="secondary" type="button" onClick={toggleClassroomMode}>
                退出课堂
              </Button>
            ) : null}
            <div className="topbar-more">
              <details className="topbar-menu">
                <summary aria-label="更多操作">⋯</summary>
                <div className="topbar-menu-panel">
                  {canManageOrg(state) ? (
                    <button type="button" onClick={() => navigate("/admin")}>
                      管理台
                    </button>
                  ) : null}
                  <button type="button" className="teacher-pill-inline" disabled>
                    {state.currentTeacher?.name || "老师"}
                  </button>
                  <button type="button" onClick={() => { if (canLeavePage()) logout.mutate(); }}>
                    退出登录
                  </button>
                </div>
              </details>
              <div className="topbar-actions-wide">
                {canManageOrg(state) ? (
                  <Button className="topbar-action-button" variant="secondary" type="button" onClick={() => navigate("/admin")}>
                    管理台
                  </Button>
                ) : null}
                <div className="teacher-pill" aria-label={`当前教师：${state.currentTeacher?.name || "老师"}`}>
                  <span className="teacher-avatar" aria-hidden="true">
                    {(state.currentTeacher?.name || "老师").trim().slice(0, 1)}
                  </span>
                  <span className="teacher-pill-name">{state.currentTeacher?.name || "老师"}</span>
                </div>
                <Button className="topbar-action-button" variant="secondary" onClick={() => { if (canLeavePage()) logout.mutate(); }}>
                  退出
                </Button>
              </div>
            </div>
          </div>
        </header>
        <Suspense fallback={<LoadingBlock />}><Routes>
          <Route path="/" element={<Overview state={state} />} />
          <Route path="/students" element={<StudentsPage state={state} />} />
          <Route path="/rewards" element={<RewardsPage key={`${state.organizationId}-${state.classId}`} state={state} />} />
          <Route path="/points" element={<PointsPage state={state} />} />
          <Route path="/leaderboard" element={<Leaderboard state={state} />} />
          <Route path="/history" element={<HistoryPage state={state} />} />
          <Route path="/counseling" element={<CounselingPage state={state} />} />
          <Route path="/growth" element={<GrowthPage state={state} />} />
          <Route path="/goals" element={<GoalsPage state={state} />} />
          <Route path="/attendance" element={<AttendancePage state={state} />} />
          <Route path="/reports" element={<ReportsPage state={state} />} />
          <Route path="/grades" element={<GradesPage state={state} />} />
          <Route path="/monthly" element={<MonthlyPage state={state} />} />
          <Route path="/insights" element={<InsightsPage state={state} />} />
          <Route path="/classroom-tools" element={<ClassroomToolsPage state={state} />} />
          <Route path="/settings" element={<SettingsPage state={state} />} />
          <Route path="*" element={<Overview state={state} />} />
        </Routes></Suspense>
      </main>
      <nav className="mobile-nav" aria-label="移动端导航">
        {mobileNavItems.map(([path, label, icon]) => (
          <NavLink key={path} to={path} end={path === "/"} onClick={() => setMoreOpen(false)}>
            <img src={icon} alt="" />
            <span>{label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={`mobile-nav-more${moreOpen || moreActive ? " active" : ""}`}
          aria-expanded={moreOpen}
          aria-controls="mobile-more-sheet"
          onClick={() => setMoreOpen((open) => !open)}
        >
          <img src={image.mobileMore} alt="" />
          <span>更多</span>
        </button>
      </nav>
      {moreOpen ? (
        <div className="mobile-more-backdrop" onClick={() => setMoreOpen(false)} aria-hidden="true" />
      ) : null}
      {moreOpen ? (
        <div id="mobile-more-sheet" className="mobile-more-sheet open" role="dialog" aria-modal="true" aria-label="更多页面">
          <div className="mobile-more-grid">
            {visibleMoreItems.map(([path, label, icon]) => (
              <NavLink key={path} to={path} onClick={() => setMoreOpen(false)}>
                <img src={icon} alt="" />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
